import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { Page } from "../components/page";
import { getUser, type User } from "../api/bsso";
import { ServerConfig } from "../interfaces/server-config";

/**
 * Clears the redirecting flag from the URL if it exists
 */
const clearRedirectingFlag = (): void => {
  if (window.location.search.includes("redirecting=true")) {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("redirecting");

    // Use replaceState to avoid adding to browser history
    window.history.replaceState({}, "", newUrl.toString());
  }
};

const Home = lazy(() => import("./home"));
const SignIn = lazy(() => import("./signin"));
const NotAuthorized = lazy(() => import("./not-authorized"));
const NotFound = lazy(() => import("./not-found"));
const UploadAsset = lazy(() => import("./upload-asset"));
const Gallery = lazy(() => import("./gallery"));

export const customRouter = (config: ServerConfig) => {
  const userLoader = async (): Promise<User | null> => {
    try {
      // Check if we're already in the process of redirecting to avoid loops
      if (window.location.search.includes("redirecting")) {
        // Clear the redirecting flag immediately to prevent permanent auth-skipping
        // The redirect should have already completed by the time this loader runs
        // If not, the auth flow will restart properly on the next navigation
        clearRedirectingFlag();

        return null;
      }

      const user = await getUser();

      // If no user but we're coming from BSSO callback or navigating to protected route,
      // wait a moment and retry to allow session to be established
      if (
        !user &&
        (window.location.pathname.includes("/upload_") ||
          document.referrer.includes("bsso"))
      ) {
        // Small delay to allow session to be established
        await new Promise((resolve) => setTimeout(resolve, 200));
        const retryUser = await getUser();

        // Always clear the redirecting flag to prevent indefinite skipping
        clearRedirectingFlag();

        return retryUser;
      }

      // Always clear the redirecting flag to prevent indefinite skipping
      clearRedirectingFlag();

      return user;
    } catch (error) {
      console.error("User loader failed:", error);
      // Always clear the redirecting flag to prevent indefinite skipping
      clearRedirectingFlag();
      return null; // Allow the app to continue even if auth fails
    }
  };

  return createBrowserRouter(
    [
      // Sign-in route - standalone without Page wrapper
      {
        path: "/signin",
        Component: SignIn,
      },
      // Access denied - standalone without auth requirement
      {
        path: "/403",
        Component: NotAuthorized,
      },
      // Main application routes with authentication
      {
        id: "root",
        Component: Page,
        loader: userLoader,
        children: [
          {
            path: "*",
            Component: NotFound,
          },
          {
            index: true,
            Component: Home,
          },
          {
            path: "upload_asset/:folderName",
            Component: UploadAsset,
            loader: () => {
              return {
                payloadSizeLimit: config.payloadSizeLimit,
                websocketChunkSize: config.websocketChunkSize,
                isOutsideFacing: config.isOutsideFacing,
              };
            },
          },
          ...(config.enableAssetGalleryView
            ? [
                {
                  path: "gallery/:assetId?",
                  Component: Gallery,
                },
              ]
            : []),
        ],
      },
    ],
    {
      future: {
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_relativeSplatPath: true,
      },
    },
  );
};
